<script setup lang="ts">
// AI 教练对话区（主界面与浮窗共用同一套组件、同一份状态）
//
// 定位（计划 Task C2 / 决策记录 U1）：主界面是完整功能，浮窗只是快捷入口，
// 两者渲染的都是本组件，因此切换渲染位置时对话不丢、也不会新建会话。
//
// 数据流只有一条：会话、消息、草稿、生成态、场景提示、token 用量全部存在 stores/aiCoach.ts，
// 本组件不复制任何会话状态，只做「渲染 + 事件转发 + 保存动作处理」：
// - 输入框直接 v-model 到 store.draft；
// - 发送/停止调用 store.send() / store.stop()；
// - 保存动作（judge_parse_save）的处理内聚在这里（原先写在浮窗里），浮窗与主界面共用一份。
//
// variant 只影响样式与高度策略：
// - panel：嵌在浮窗卡片 / 移动端整屏抽屉里（外层给高度，输入区 sticky 贴底）；
// - page：主界面右侧整栏（铺满外层容器）。

import { computed, nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { LoadingIcon } from "tdesign-icons-vue-next";
import type { AiAction } from "@/apis/ai.ts";
import { useAiCoachStore } from "@/stores/aiCoach.ts";
import type { AiCoachMessage } from "@/stores/aiCoach.ts";
import { SplitPromptKeywords } from "@/stores/aiCoach.ts";
import { BuildAiCoachEntryPath } from "@/stores/aiCoachEntry.ts";
import { PostJudgeAiParseSave } from "@/apis/judge.ts";
import { GetText } from "@/text/library.ts";
import { useCurrentInstance } from "@/util";
import { ShowTextTipsError, ShowTextTipsSuccess } from "@/util/tips";
import AiMessageList from "@/components/ai/AiMessageList.vue";

const props = withDefaults(
  defineProps<{
    /** panel：浮窗内嵌；page：主界面右侧整栏。只影响样式与高度策略，逻辑一份 */
    variant?: "panel" | "page";
  }>(),
  { variant: "panel" },
);

const emit = defineEmits<{
  /** 动作卡片处理完毕（浮窗据此在移动端关闭整屏抽屉，主界面不需要额外处理） */
  (e: "handled", action: AiAction): void;
}>();

const store = useAiCoachStore();
const router = useRouter();
const route = useRoute();
const { globalProperties } = useCurrentInstance();

const messageListRef = ref<InstanceType<typeof AiMessageList> | null>(null);
/** t-textarea 暴露了 focus()（tdesign Textarea 的 expose），这里只用到聚焦 */
const inputRef = ref<{ focus: () => void } | null>(null);

const isPanel = computed(() => props.variant === "panel");

const modelText = computed(() => {
  const parts = [store.provider, store.model].filter(Boolean);
  return parts.length > 0 ? `：${parts.join(" / ")}` : "";
});

const usageText = computed(() => {
  const usage = store.lastUsage;
  if (!usage) {
    return "";
  }
  // cached_tokens 由后端 done 事件下发：未命中或厂商不支持缓存时为 0
  const cached = usage.cached_tokens ?? 0;
  const base = `本轮 token：${usage.total_tokens}（输入 ${usage.prompt_tokens}，其中缓存命中 ${cached} / 输出 ${usage.completion_tokens}）`;
  // 命中占比只在确实命中且输入长度有效时追加：cached 为 0 时不显示占比，避免「0%」被误读为缓存失效
  if (cached <= 0 || usage.prompt_tokens <= 0) {
    return base;
  }
  return `${base}（${Math.round((cached / usage.prompt_tokens) * 100)}%）`;
});

const errorText = computed(() => store.errorMessage);
const canRetry = computed(() => store.messages.some((message) => message.role === "user"));
const canSend = computed(
  () => store.draft.trim().length > 0 && !store.generating && store.sceneEnabled && !store.sceneNotice,
);
// 场景提示（详情页带来的禁用原因）非空时禁用输入：不让用户先发一条注定失败的消息
const inputDisabled = computed(() => store.generating || !store.sceneEnabled || !!store.sceneNotice);

const inputPlaceholder = computed(() => {
  if (store.sceneNotice) {
    return "当前对象暂不可使用该场景，请查看上方提示";
  }
  if (!store.sceneEnabled) {
    return "当前场景不可用，暂时无法对话";
  }
  if (store.generating) {
    return "AI 正在生成中，可点击「停止」中断";
  }
  return "描述你的问题（Enter 发送 / Shift+Enter 换行）";
});

/**
 * 模板排序：命中关键字的排到最前，其余保持后端原顺序。
 * 用「稳定分桶」而不是 sort：sort 的比较函数对「都不命中」的元素返回 0 时，
 * 虽然现代引擎保证稳定，但显式分桶更能表达意图，也避免将来有人加比较规则时打乱后端顺序。
 */
function sortPromptsByKeywords(prompts: string[], keywords: string): string[] {
  const matchers = SplitPromptKeywords(keywords);
  if (matchers.length === 0) {
    return prompts;
  }
  const hit: string[] = [];
  const rest: string[] = [];
  prompts.forEach((prompt) => {
    if (matchers.some((keyword) => prompt.includes(keyword))) {
      hit.push(prompt);
    } else {
      rest.push(prompt);
    }
  });
  return [...hit, ...rest];
}

/**
 * 模板 chips：模板一律由后端场景定义下发（/ai/scene 的 prompts，可选字段），
 * 后端未下发时为 0 条 → 整块不渲染（不占位、不报错）。
 *
 * 排序（Task C4 / 决策记录 U2）：按当前上下文的 promptKeywords 做「包含匹配」，
 * 命中的模板排到最前，其余**保持后端顺序且不隐藏**——用户仍然可以看到并使用全部模板。
 * 后端模板是刻意写的中性措辞，关键字匹配落空时只是不排序，不会少一个 chip。
 */
const promptChips = computed(() => {
  const meta = store.sceneList.find((item) => item.key === store.scene);
  const prompts = (meta?.prompts ?? []).filter((item) => typeof item === "string" && item.trim().length > 0);
  return sortPromptsByKeywords(prompts, store.promptKeywords);
});

// ── 上下文清单（「它现在能看到什么」）──
//
// 数据来源与 store 同源：打开浮窗时 /ai/scene 的预览（发问之前就有）与对话首帧的 context 事件。
// 这里只做「分组 + 折叠」两件事，label / note 一律来自服务端：
// 前端一旦自造文案，「页面上写的」与「实际注入的」迟早分叉，而清单的全部价值就在一致。
//
// 折叠态只保留一行摘要（页面锚点 + 分组计数），不铺开条目——详情由用户点击展开。

/** 分组标签是 kind 的固定呈现（与场景无关），所以留在前端；清单内容不在前端拼 */
const CONTEXT_GROUP_TITLES = {
  injected: "已注入",
  onDemand: "可按需查询",
  blocked: "不可见",
} as const;

const contextExpanded = ref(false);

/** 「改用当前页面」：按当前页面重新打开教练（场景/对象变化时开新会话，旧对话在历史里） */
async function handleRebindToCurrentPage() {
  // 真实路径由本组件取（store 不依赖 router）：重绑后新会话要按「当前页面」统计
  await store.rebindToCurrentPage(BuildAiCoachEntryPath(route));
}

/** 模板是否命中当前上下文的关键字（命中者排前并高亮，让「为什么是它排前面」可见） */
function promptHitKeyword(prompt: string): boolean {
  const matchers = SplitPromptKeywords(store.promptKeywords);
  return matchers.length > 0 && matchers.some((keyword) => prompt.includes(keyword));
}

/**
 * 有清单项就展示；没有清单项但预览回了失败原因时也展示（让用户知道为什么用不了）。
 *
 * 例外：页面已经带了场景提示（sceneNotice，如提交详情页的「比赛进行中」）时不再重复——
 * 同一件事在两处各说一遍，只会把真正要看的信息淹掉。
 * 但漂移（pageDrifted）时上下文条还承担「基于哪个页面、当前在哪个页面」的说明与
 * 「改用当前页面」入口，必须始终展示，不能被 sceneNotice 的例外吞掉。
 */
const contextVisible = computed(
  () =>
    store.pageDrifted ||
    store.contextItems.length > 0 ||
    (store.contextCode > 0 && !store.sceneNotice),
);

/** 折叠态的一行摘要：三个分组的计数（「可按需查询」缩成「可查」，一行内更简洁） */
const contextSummary = computed(() => {
  const groups = store.contextGroups;
  return `已注入 ${groups.injected.length} · 可查 ${groups.onDemand.length} · 不可见 ${groups.blocked.length}`;
});

/**
 * 页面锚点说明（「上下文属于哪个页面」）：
 * - 正常：在「XX」打开；
 * - 漂移：对话仍基于打开时的页面，说明更新为「基于 XX，当前在 YY」——只更新说明、不弹警告，
 *   配合「改用当前页面」按钮把「上下文可能对不上眼前页面」这件事讲清楚。
 */
const contextAnchorText = computed(() => {
  if (store.pageDrifted && store.pageDriftEntry) {
    const current = store.pageDriftEntry.title;
    return store.entryPageTitle ? `基于「${store.entryPageTitle}」，当前在「${current}」` : `当前在「${current}」`;
  }
  return store.entryPageTitle ? `在「${store.entryPageTitle}」打开` : "";
});

const contextDetailGroups = computed(() => {
  const groups = store.contextGroups;
  return [
    { key: "injected", title: CONTEXT_GROUP_TITLES.injected, items: groups.injected },
    { key: "onDemand", title: CONTEXT_GROUP_TITLES.onDemand, items: groups.onDemand },
    { key: "blocked", title: CONTEXT_GROUP_TITLES.blocked, items: groups.blocked },
  ].filter((group) => group.items.length > 0);
});

/** 是否有可展开的详情：清单为空、只有失败原因时折叠态直接显示原因，头部不可展开 */
const hasContextDetail = computed(() => contextDetailGroups.value.length > 0);

/** 展开/收起上下文详情（无详情时头部只是信息条，不可点） */
function toggleContextDetail() {
  if (!hasContextDetail.value) {
    return;
  }
  contextExpanded.value = !contextExpanded.value;
}

/** 键盘可达：头部行按 Enter/Space 展开；事件源自内部按钮（如「改用当前页面」）时不处理 */
function handleContextHeadKeydown(event: KeyboardEvent) {
  if (event.target !== event.currentTarget) {
    return;
  }
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    toggleContextDetail();
  }
}

/** 预览不可用的原因：错误码优先查项目文案表（与其它 AI 错误同一套范式） */
const contextErrorText = computed(() => {
  if (!store.contextCode) {
    return "";
  }
  const [found, text] = GetText(store.contextCode);
  return found ? text : "当前对象的上下文暂不可用";
});

// ── 聚焦与滚动 ──

/** 聚焦输入框（浮窗的 open/新对话、主界面的新对话都会调用） */
async function focusInput() {
  await nextTick();
  inputRef.value?.focus();
}

// 浮窗形态才跟随 store 的聚焦/展开信号：主界面自己控制聚焦，避免两处抢焦点
watch(
  () => store.focusSignal,
  () => {
    if (isPanel.value) {
      void focusInput();
    }
  },
);

watch(
  () => store.visible,
  (visible) => {
    if (!isPanel.value || !visible) {
      return;
    }
    void focusInput();
    // 重新展开时贴到底部（此前可能在别处滚动过）
    void messageListRef.value?.scrollToBottom(true);
  },
);

// ── 交互 ──

/**
 * Enter 发送 / Shift+Enter 换行。
 * t-textarea 的 onKeydown 是 tdesign 自己的签名（value + { e }），不是原生事件对象；
 * 输入法组合中的回车不发送（与 MessageView 的处理一致）。
 */
function handleKeydown(_value: string | number, context: { e: KeyboardEvent }) {
  const event = context.e;
  if (event.isComposing || event.key !== "Enter" || event.shiftKey) {
    return;
  }
  event.preventDefault();
  handleSend();
}

function handleSend() {
  if (!canSend.value) {
    return;
  }
  void store.send();
}

function handleStop() {
  store.stop();
}

/** 错误提示旁的「重新发送」：把最后一条用户消息再发一次 */
async function handleRetry() {
  const lastUser = [...store.messages].reverse().find((message) => message.role === "user");
  if (!lastUser) {
    return;
  }
  await store.send(lastUser.content);
}

/** 未配置 BYOK：跳用户设置页的「AI 大模型」页签（?tab=8，页签值由设置页做白名单校验） */
function handleGotoSettings() {
  void router.push({ name: "user-modify", query: { tab: "8" } });
}

/** 点击模板：只填充输入框（可继续编辑），不自动发送 */
async function handleApplyPrompt(prompt: string) {
  store.draft = prompt;
  await focusInput();
}

// ── 保存动作（judge_parse_save）──

/**
 * 失败文案：错误码优先查项目文案表。
 * 100093 的表文案即「该提交已有 AI 解析，仅提交者可覆盖」，这里再兜一层同义原文，
 * 保证即使文案表缺项也能明确告知原因。
 */
function resolveSaveErrorText(code: number): string {
  if (code > 0) {
    const [found, text] = GetText(code);
    if (found) {
      return text;
    }
  }
  if (code === 100093) {
    return "该提交已有解析，仅提交者可覆盖";
  }
  return "保存失败，请稍后重试";
}

/**
 * 保存动作处理（judge_parse_save）：
 * 正文取「卡片所在气泡的正文」——即该条 assistant 消息的 content（服务端不持有草稿，只能由前端回传）；
 * 凭证取 payload.receipt（生成正常结束时服务端签发的加密回执，30 分钟有效、与本用户绑定）。
 *
 * 历史消息里的卡片没有 receipt：不调用接口（必然失败），由 AiActionCard 直接禁用并说明，
 * 这里再做一层防御，避免任何入口真的发出无效请求。
 */
async function handleSaveAction(action: AiAction, message: AiCoachMessage) {
  const payload = action.payload ?? {};
  const receipt = typeof payload.receipt === "string" ? payload.receipt.trim() : "";
  const judgeId = Number(payload.judge_id ?? (store.ref.type === "judge" ? store.ref.id : 0)) || 0;
  if (!receipt) {
    ShowTextTipsError(globalProperties, "该保存凭证已失效，请重新生成解析后再保存");
    return;
  }
  if (!judgeId) {
    ShowTextTipsError(globalProperties, "找不到要保存的提交，请在提交详情页重新生成解析");
    return;
  }
  const content = message.content ?? "";
  if (!content.trim()) {
    ShowTextTipsError(globalProperties, "解析正文为空，无法保存");
    return;
  }
  try {
    const res = await PostJudgeAiParseSave(judgeId, content, receipt);
    if (res.code !== 0) {
      ShowTextTipsError(globalProperties, resolveSaveErrorText(res.code));
      return;
    }
    // 成功：卡片置为「已保存」禁用态（动作对象来自 store 消息，改字段即触发重渲染）
    store.markActionSaved(action);
    ShowTextTipsSuccess(globalProperties, "已保存为本次提交的解析");
  } catch (e) {
    console.error("save judge ai parse failed:", e);
    ShowTextTipsError(globalProperties, "保存失败，请稍后重试");
  }
}

/** 动作卡片处理完毕：向上抛出，由渲染位置（浮窗/主界面）决定是否需要额外收尾 */
function handleActionHandled(action: AiAction) {
  emit("handled", action);
}

defineExpose({ focusInput });
</script>

<template>
  <div class="ai-chat" :class="`ai-chat--${variant}`">
    <AiMessageList
      ref="messageListRef"
      :messages="store.messages"
      :generating="store.generating"
      :save-handler="handleSaveAction"
      @handled="handleActionHandled"
    />

    <div class="ai-chat-foot">
      <!-- 场景提示（详情页带来的禁用原因）优先于场景不可用与请求错误 -->
      <t-alert v-if="store.sceneNotice" theme="warning" :message="store.sceneNotice" />
      <t-alert v-else-if="!store.sceneEnabled" theme="warning" message="当前场景不可用，暂时无法继续对话" />
      <div v-else-if="errorText" class="ai-chat-error">
        <t-alert theme="error" :message="errorText" />
        <t-button v-if="canRetry" size="small" variant="text" theme="primary" @click="handleRetry">重新发送</t-button>
      </div>

      <div v-if="!store.byokReady" class="ai-chat-byok">
        <t-icon name="info-circle" />
        <span>未配置 AI 密钥与模型，发送后会提示去设置</span>
        <t-button size="small" variant="text" theme="primary" @click="handleGotoSettings">去设置</t-button>
      </div>

      <!--
        上下文条：回答「它现在能看到什么」。
        两个来源同源：打开浮窗时 /ai/scene 的预览、对话首帧的 context 事件（真正发给模型的那一份）。
        折叠态只保留一行：页面锚点（漂移时更新为「基于 XX，当前在 YY」并给出「改用当前页面」入口）
        + 分组计数；点击展开才按分组列出每一项的说明。
      -->
      <div v-if="contextVisible" class="ai-chat-context">
        <div
          class="ai-chat-context-head"
          :class="{ 'is-drifted': store.pageDrifted, 'is-plain': !hasContextDetail }"
          role="button"
          :tabindex="hasContextDetail ? 0 : undefined"
          @click="toggleContextDetail"
          @keydown="handleContextHeadKeydown"
        >
          <t-icon v-if="hasContextDetail" :name="contextExpanded ? 'chevron-down' : 'chevron-right'" />
          <span class="ai-chat-context-title">本次上下文</span>
          <span v-if="!hasContextDetail && contextErrorText" class="ai-chat-context-anchor is-error">
            {{ contextErrorText }}
          </span>
          <span v-else-if="contextAnchorText" class="ai-chat-context-anchor">{{ contextAnchorText }}</span>
          <span v-if="!store.pageDrifted && hasContextDetail" class="ai-chat-context-summary">{{ contextSummary }}</span>
          <t-button
            v-if="store.pageDrifted"
            class="ai-chat-context-rebind"
            size="small"
            variant="text"
            theme="warning"
            :disabled="store.generating"
            @click.stop="handleRebindToCurrentPage"
          >
            改用当前页面
          </t-button>
        </div>

        <div v-if="contextExpanded && hasContextDetail" class="ai-chat-context-detail">
          <div v-for="group in contextDetailGroups" :key="group.key" class="ai-chat-context-group">
            <div class="ai-chat-context-group-title">{{ group.title }}（{{ group.items.length }}）</div>
            <div v-for="(item, index) in group.items" :key="`${group.key}-${index}`" class="ai-chat-context-item">
              <span class="ai-chat-context-item-label">
                {{ item.label }}<em v-if="item.ref">{{ item.ref }}</em>
              </span>
              <span v-if="item.note" class="ai-chat-context-item-note">{{ item.note }}</span>
            </div>
          </div>
          <div v-if="contextErrorText" class="ai-chat-context-item-note">{{ contextErrorText }}</div>
        </div>
      </div>

      <!--
        模板 chips：数据来自 /ai/scene 的 prompts（模板一律由后端下发，前端只排序与填充），
        未下发时为 0 条 → 整块不渲染（不占位、不报错）。
        prompts 插槽拿到的是**已按上下文排好序**的列表：调用方需要替换渲染时覆盖默认内容即可。
      -->
      <div v-if="promptChips.length > 0" class="ai-chat-prompts">
        <slot name="prompts" :prompts="promptChips" :apply="handleApplyPrompt">
          <button
            v-for="prompt in promptChips"
            :key="prompt"
            type="button"
            class="ai-chat-prompt"
            :class="{ 'is-hit': promptHitKeyword(prompt) }"
            :disabled="inputDisabled"
            :title="promptHitKeyword(prompt) ? `${prompt}（按当前对象推荐）` : prompt"
            @click="handleApplyPrompt(prompt)"
          >
            {{ prompt }}
          </button>
        </slot>
      </div>

      <div class="ai-chat-input-row">
        <t-textarea
          ref="inputRef"
          v-model="store.draft"
          class="ai-chat-input"
          :autosize="{ minRows: 2, maxRows: 6 }"
          :disabled="inputDisabled"
          :placeholder="inputPlaceholder"
          @keydown="handleKeydown"
        />
        <t-button v-if="store.generating" theme="danger" size="small" class="ai-chat-send" @click="handleStop">
          <template #icon><LoadingIcon /></template>
          停止
        </t-button>
        <t-button v-else theme="primary" size="small" class="ai-chat-send" :disabled="!canSend" @click="handleSend">
          <template #icon><t-icon name="send" /></template>
          发送
        </t-button>
      </div>

      <div class="ai-chat-foot-meta">
        <span>当前使用你配置的密钥与模型{{ modelText }}</span>
        <span v-if="usageText" class="ai-chat-foot-usage">{{ usageText }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 对话区：消息列表撑满，输入区固定在底部（两种形态同一套结构） */
.ai-chat {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.ai-chat--page {
  background-color: var(--td-bg-color-container);
}

/* 输入区：移动端软键盘弹出时仍贴在容器底部 */
.ai-chat-foot {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px 10px;
  border-top: 1px solid var(--td-component-border);
  background-color: var(--td-bg-color-container);
  /* 视口极矮时卡片被压到贴视口：内容高于剩余空间时内部滚动，保证发送按钮永远可达 */
  min-height: 0;
  overflow-y: auto;
}

/* 主界面空间更宽裕，给输入区多一点呼吸 */
.ai-chat--page .ai-chat-foot {
  gap: 8px;
  padding: 10px 16px 12px;
}

.ai-chat-error {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-chat-error :deep(.t-alert) {
  flex: 1;
}

.ai-chat-byok {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--td-text-color-secondary);
}

/* 模板 chips（后端未下发 prompts 时整块不渲染） */
.ai-chat-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* 上下文条：折叠态一行摘要（锚点 + 计数），展开态按分组列出每一项 */
.ai-chat-context {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-default);
  background-color: var(--td-bg-color-secondarycontainer);
}

.ai-chat-context-head {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  color: var(--td-text-color-secondary);
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  user-select: none;
}

/* 没有可展开的详情（只有失败原因）时头部只是信息条，不摆出可点的样子 */
.ai-chat-context-head.is-plain {
  cursor: default;
}

.ai-chat-context-title {
  flex: none;
  font-weight: 600;
  color: var(--td-text-color-primary);
}

/* 页面锚点：核对上下文一致性的锚点（正常在「XX」打开；漂移时更新为「基于 XX，当前在 YY」） */
.ai-chat-context-anchor {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 漂移：锚点说明用警示色，说明「上下文属于哪个页面」已经过期（不再是整条警告横幅） */
.ai-chat-context-head.is-drifted .ai-chat-context-anchor {
  color: var(--td-warning-color);
}

/* 上下文不可用：错误原因用错误色 */
.ai-chat-context-anchor.is-error {
  color: var(--td-error-color);
}

.ai-chat-context-summary {
  flex: none;
  color: var(--td-text-color-placeholder);
  white-space: nowrap;
}

/* 「改用当前页面」：窄卡片里也不收缩折行（锚点文字负责让位 ellipsis） */
.ai-chat-context-rebind {
  flex: none;
}

.ai-chat-context-detail {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-chat-context-group-title {
  font-size: 11px;
  color: var(--td-text-color-placeholder);
}

.ai-chat-context-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-left: 8px;
}

.ai-chat-context-item-label {
  font-size: 12px;
  color: var(--td-text-color-primary);
}

/* ref 只说明「指的是哪一个」，弱化处理，不抢 label 的位置 */
.ai-chat-context-item-label em {
  margin-left: 4px;
  font-style: normal;
  color: var(--td-text-color-placeholder);
}

.ai-chat-context-item-note {
  font-size: 11px;
  line-height: 1.5;
  color: var(--td-text-color-placeholder);
}

.ai-chat-prompt {
  max-width: 100%;
  padding: 2px 10px;
  border: 1px solid var(--td-component-border);
  border-radius: 999px;
  background-color: var(--td-bg-color-secondarycontainer);
  color: var(--td-text-color-secondary);
  font-size: 12px;
  line-height: 1.8;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-chat-prompt:hover:not(:disabled) {
  border-color: var(--td-brand-color);
  color: var(--td-brand-color);
}

/* 命中当前上下文关键字的推荐（排在前面的那几条）：加粗边框提示「这是按当前对象推荐的」 */
.ai-chat-prompt.is-hit {
  border-color: var(--td-brand-color);
  color: var(--td-brand-color);
  background-color: var(--td-brand-color-light);
}

.ai-chat-prompt:disabled {
  color: var(--td-text-color-disabled);
  cursor: not-allowed;
}

.ai-chat-input-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

/* class 落在 t-textarea 的外层容器上（tdesign 把 attrs 透传到根元素），内层 textarea 用 :deep 调整 */
.ai-chat-input {
  flex: 1;
  min-width: 0;
}

.ai-chat-input :deep(.t-textarea__inner) {
  min-height: 46px;
  max-height: 140px;
  padding: 6px 8px;
  background-color: var(--td-bg-color-container);
  color: var(--td-text-color-primary);
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
}

.ai-chat-input :deep(.t-textarea__inner:disabled) {
  background-color: var(--td-bg-color-container-hover);
  color: var(--td-text-color-disabled);
  cursor: not-allowed;
}

.ai-chat-send {
  flex: none;
}

.ai-chat-foot-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: var(--td-text-color-placeholder);
}

.ai-chat-foot-usage {
  flex: none;
}
</style>
